/**
 * Deterministic trim for a Suno submission that came in over the combined
 * character budget -- shrinks the {LAYER-N ...} physics blocks (vocal +
 * instrument behavior tags), never the actual song lyrics/structure.
 *
 * VocalForge/InstrumentForge physics tags are written as one comma-separated
 * clause list per [Label: ...] line (see lyricsPostProcessor.ts's
 * postProcessPhysicsLayers, which is what stamps these blocks into the
 * lyrics in the first place). This drops trailing clauses until the whole
 * document fits, never emptying a line to zero clauses and never touching
 * anything past the bleed-token line (three slashes, asterisks, three
 * slashes) that separates physics metadata from the actual sung/structural
 * content.
 *
 * Layer-1 (VOCAL PHYSICS) is what makes a custom voice sound like that
 * voice rather than a generic default -- a first version picked "whichever
 * trimmable line is currently longest" globally, which meant Layer-1's
 * Vocal_Behavior line (by far the longest/densest of any tag) absorbed 8 of
 * 9 dropped clauses on a real generation while every instrument line stayed
 * almost untouched. The result technically fit the char budget but the
 * voice came out sounding like a generic baritone instead of the user's
 * built profile (Forgejo, 2026-09-23). Layer-2/3/4/5 (instrument physics)
 * are trimmed first and hardest; Layer-1 is only touched once every other
 * layer is already down to one clause per line. Within whichever layer is
 * currently being trimmed, cuts round-robin across its lines one clause at
 * a time rather than hammering a single line -- so even Layer-1, if it must
 * be touched, loses breadth evenly across Vocal_Behavior/Lead_Vocal/Harmony/
 * Choir instead of gutting one of them to near nothing.
 */

const LAYER_HEADER_RE = /^\{LAYER-(\d)[^}]*\}$/i;
const TAG_LINE_RE = /^\[([^:\]]+):\s*(.+)\]$/;
const BLEED_TOKEN_LINE_RE = /^\/\/\/\*+\/\/\/$/;

interface TrimmableLine {
  lineIndex: number;
  label: string;
  clauses: string[];
  layer: number;
}

export interface PhysicsTrimResult {
  text: string;
  finalChars: number;
  trimmed: boolean;
  clausesDropped: number;
  /** >0 if still over targetMax after every trimmable clause down to 1 per line was exhausted. */
  stillOverBy: number;
}

/**
 * Find the trimmable physics-block lines. The zone starts at the first
 * {LAYER-N ...} header -- generations can carry unrelated bracket lines
 * before it (a Riff DNA/solo-choreography cue dropped at the very top, say)
 * that don't fit the physics-zone shape but aren't lyrics either, so this
 * skips forward to the real start rather than bailing on the first line.
 * Once inside the zone it stops at the bleed token or the first line that
 * doesn't fit the shape -- never guess past that boundary into real lyrics.
 */
function findPhysicsZone(lines: string[]): TrimmableLine[] {
  const trimmable: TrimmableLine[] = [];
  const zoneStart = lines.findIndex(l => LAYER_HEADER_RE.test(l.trim()));
  if (zoneStart === -1) return trimmable;

  let currentLayer = 1;
  for (let i = zoneStart; i < lines.length; i++) {
    const line = lines[i].trim();
    if (BLEED_TOKEN_LINE_RE.test(line)) break;
    if (!line) continue;

    const headerMatch = line.match(LAYER_HEADER_RE);
    if (headerMatch) { currentLayer = parseInt(headerMatch[1], 10); continue; }

    const tagMatch = line.match(TAG_LINE_RE);
    if (tagMatch) {
      trimmable.push({
        lineIndex: i,
        label: tagMatch[1],
        clauses: tagMatch[2].split(', ').map(c => c.trim()).filter(Boolean),
        layer: currentLayer,
      });
      continue;
    }

    // Anything else (a [Section] tag, free lyric text, etc.) ends the zone --
    // never guess past here, even without a bleed token.
    break;
  }

  return trimmable;
}

function applyLine(lines: string[], t: TrimmableLine): number {
  const newLineContent = `[${t.label}: ${t.clauses.join(', ')}]`;
  const delta = newLineContent.length - lines[t.lineIndex].length;
  lines[t.lineIndex] = newLineContent;
  return delta;
}

export function trimPhysicsBlocksToFit(fullText: string, targetMax: number): PhysicsTrimResult {
  if (fullText.length <= targetMax) {
    return { text: fullText, finalChars: fullText.length, trimmed: false, clausesDropped: 0, stillOverBy: 0 };
  }

  const lines = fullText.split('\n');
  const trimmable = findPhysicsZone(lines);

  if (trimmable.length === 0) {
    return { text: fullText, finalChars: fullText.length, trimmed: false, clausesDropped: 0, stillOverBy: fullText.length - targetMax };
  }

  let clausesDropped = 0;
  let currentLength = fullText.length;

  // Least-protected layer first (highest layer number = instrument/atmosphere),
  // vocal physics (layer 1) last.
  const layers = [...new Set(trimmable.map(t => t.layer))].sort((a, b) => b - a);

  for (const layer of layers) {
    if (currentLength <= targetMax) break;
    const group = trimmable.filter(t => t.layer === layer);

    // Round-robin within this layer: one clause per line per pass, so no
    // single line in the group absorbs a disproportionate share of the cut.
    let madeProgress = true;
    while (currentLength > targetMax && madeProgress) {
      madeProgress = false;
      for (const t of group) {
        if (currentLength <= targetMax) break;
        if (t.clauses.length <= 1) continue;
        t.clauses.pop();
        clausesDropped++;
        currentLength += applyLine(lines, t);
        madeProgress = true;
      }
    }
  }

  const text = lines.join('\n');
  return {
    text,
    finalChars: text.length,
    trimmed: clausesDropped > 0,
    clausesDropped,
    stillOverBy: Math.max(0, text.length - targetMax),
  };
}
